import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { alpha } from '@mui/material/styles';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { type ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { TrackerDataType, Tone } from '../../shared/constants/constants';
import { appPalette, logPalette, neutralPalette } from '../../theme/palette';
import { fontWeights, semanticTypography, typography, typographyColors } from '../../theme/typography';
import dayjs from '../../utils/dayjs';
import CalendarSnapshotChip, { calendarSnapshotChipTypography } from './CalendarSnapshotChip';
import CalendarTypeChip from './CalendarTypeChip';
import { calendarModalCategoryPalette, calendarStatusPalette, calendarUiPalette } from './calendarPalette';
import { CalendarEvent, CalendarEventChipItem, CalendarEventLinkedItem } from './types';

interface EventModalProps {
  event: CalendarEvent | null;
  onClose: () => void;
}

type RenderableItem = string | CalendarEventChipItem | CalendarEventLinkedItem | null | undefined;

const categoryLabelMap = {
  [TrackerDataType.SERVICE]: 'Service Log',
  [TrackerDataType.MEDIA_INTERACTION]: 'Media Interaction',
  [TrackerDataType.ENGAGEMENT]: 'Engagement',
  [TrackerDataType.CONTENT_LOG]: 'Content Log',
} as const;

const MODAL_EDGE_GAP = 8;
const MODAL_OUTER_GAP_PX = MODAL_EDGE_GAP * 8;
const PREVIEW_MAX_CHARS = 220;
const DEFAULT_VISIBLE_CHIPS = 4;

const sectionLabelSx = {
  ...typography.overline,
  fontSize: '11px',
  lineHeight: '15px',
  fontWeight: fontWeights.medium,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: calendarUiPalette.eventModal.sectionLabel,
} as const;

const detailTextSx = {
  ...typography.body2,
  color: typographyColors.primary,
  fontSize: '12px',
  lineHeight: 1.55,
} as const;

const headerMetaTextSx = {
  ...typography.body2,
  color: typographyColors.muted,
  fontSize: detailTextSx.fontSize,
  lineHeight: detailTextSx.lineHeight,
} as const;

const formatDateString = (date?: Date | null) => {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  return dayjs(date).format('MMM DD, YYYY');
};

const isPresent = (value?: string | null) => Boolean((value ?? '').trim());

const truncateText = (text: string, maxChars = PREVIEW_MAX_CHARS) =>
  text.length > maxChars ? `${text.slice(0, maxChars - 1).trimEnd()}…` : text;

const parsePreviewText = (value?: string | null) => {
  if (!isPresent(value)) {
    return '';
  }

  return DOMPurify.sanitize(value ?? '', { ALLOWED_TAGS: [] })
    .replaceAll(/\s+/g, ' ')
    .trim();
};

const getDetailRoute = (event: CalendarEvent) => {
  switch (event.logtype) {
    case TrackerDataType.SERVICE:
      return RouteInformation.serviceLog.path.replace(/\/$/, '') + `/${event.id}`;
    case TrackerDataType.MEDIA_INTERACTION:
      return RouteInformation.mediaInteraction.path.replace(/\/$/, '') + `/${event.id}`;
    case TrackerDataType.ENGAGEMENT:
      return RouteInformation.engagementLog.path.replace(/\/$/, '') + `/${event.id}`;
    case TrackerDataType.CONTENT_LOG:
      return RouteInformation.contentLog.path.replace(/\/$/, '') + `/${event.id}`;
    default:
      return RouteInformation.calendar.path;
  }
};

function normalizeRenderableItems(items: RenderableItem[]): CalendarEventLinkedItem[] {
  return items
    .map((item) => {
      if (!item) {
        return null;
      }

      if (typeof item === 'string') {
        const name = item.trim();
        return name ? { name } : null;
      }

      const name = `${item.name ?? ''}`.trim();
      if (!name) {
        return null;
      }

      return {
        id: 'id' in item ? item.id : undefined,
        name,
        backgroundColor: item.backgroundColor ?? undefined,
      };
    })
    .filter((item): item is CalendarEventLinkedItem => Boolean(item?.name));
}

function getVisibleItems<T>(items: T[], maxVisible = DEFAULT_VISIBLE_CHIPS) {
  return {
    visibleItems: items.slice(0, maxVisible),
    hiddenCount: Math.max(items.length - maxVisible, 0),
  };
}

function FieldBlock({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...sectionLabelSx, mb: '6px' }}>{label}</Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function getLogIcon(logtype: TrackerDataType) {
  switch (logtype) {
    case TrackerDataType.SERVICE:
      return AccountTreeOutlinedIcon;
    case TrackerDataType.MEDIA_INTERACTION:
      return NewspaperOutlinedIcon;
    case TrackerDataType.ENGAGEMENT:
      return HandshakeOutlinedIcon;
    case TrackerDataType.CONTENT_LOG:
      return DescriptionOutlinedIcon;
    default:
      return DescriptionOutlinedIcon;
  }
}

function getHeaderMetaLine(event: CalendarEvent) {
  const parts = [
    formatDateString(event.entryDate) ? `Entry ${formatDateString(event.entryDate)}` : '',
    formatDateString(event.dueDate) ? `Due ${formatDateString(event.dueDate)}` : '',
  ].filter(Boolean);

  return parts.join(' · ');
}

function getModalCategoryPalette(logtype: TrackerDataType) {
  switch (logtype) {
    case TrackerDataType.SERVICE:
      return calendarModalCategoryPalette[TrackerDataType.SERVICE];
    case TrackerDataType.MEDIA_INTERACTION:
      return calendarModalCategoryPalette[TrackerDataType.MEDIA_INTERACTION];
    case TrackerDataType.ENGAGEMENT:
      return calendarModalCategoryPalette[TrackerDataType.ENGAGEMENT];
    case TrackerDataType.CONTENT_LOG:
      return calendarModalCategoryPalette[TrackerDataType.CONTENT_LOG];
    default:
      return calendarModalCategoryPalette[TrackerDataType.SERVICE];
  }
}

function getInnerCardStyles(logtype: TrackerDataType) {
  const accentColor = getModalCategoryPalette(logtype).buttonColor;

  return {
    border: `1px solid ${alpha(neutralPalette.borderSubtle, 0.95)}`,
    borderRadius: '20px',
    backgroundColor: neutralPalette.surfaceCard,
    boxShadow: `0 10px 24px ${alpha(accentColor, 0.06)}, 0 1px 2px ${alpha(neutralPalette.black, 0.06)}`,
  };
}

function renderStatusChip(status?: string | null) {
  if (!isPresent(status)) {
    return null;
  }

  const palette = calendarStatusPalette[status as keyof typeof calendarStatusPalette] ?? calendarStatusPalette.default;

  return (
    <CalendarSnapshotChip
      label={status ?? ''}
      backgroundColor={palette.backgroundColor}
      borderColor={alpha(palette.color, 0.28)}
      color={palette.color}
    />
  );
}

function getHeaderTypeChipSx() {
  return {
    height: '26px',
    borderRadius: '10px',
    opacity: 0.92,
    '&.MuiChip-root': {
      height: '26px',
      border: '0',
      borderRadius: '10px',
    },
    '& .MuiChip-label': {
      ...calendarSnapshotChipTypography,
      px: 1.35,
      color: alpha(neutralPalette.textPrimary, 0.9),
    },
  };
}

function getOpenFullLogButtonSx(logtype: TrackerDataType) {
  const palette = getModalCategoryPalette(logtype);

  return {
    minWidth: '156px',
    minHeight: 40,
    borderRadius: '12px',
    backgroundColor: palette.buttonColor,
    color: neutralPalette.white,
    fontSize: '0.9rem',
    fontWeight: fontWeights.bold,
    textTransform: 'none',
    boxShadow: 'none',
    px: 1.5,
    '&:hover': {
      backgroundColor: palette.buttonHoverColor,
      boxShadow: 'none',
    },
    '& .MuiButton-endIcon': {
      marginLeft: 0.35,
      mr: 0.15,
    },
    '& .MuiButton-endIcon svg': {
      fontSize: '14px',
      opacity: 0.88,
    },
  } as const;
}

function renderChipCollection(
  items: RenderableItem[],
  options: {
    maxVisible?: number;
    allowAccentColor?: boolean;
    showAccentRail?: boolean;
  } = {}
) {
  const normalizedItems = normalizeRenderableItems(items);

  if (normalizedItems.length === 0) {
    return null;
  }

  const { visibleItems, hiddenCount } = getVisibleItems(normalizedItems, options.maxVisible);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'flex-start' }}>
      {visibleItems.map((item) => (
        <CalendarSnapshotChip
          key={`${item.id ?? item.name}-${item.backgroundColor ?? 'default'}`}
          label={item.name}
          backgroundColor={
            options.allowAccentColor && item.backgroundColor
              ? alpha(item.backgroundColor, 0.15)
              : calendarUiPalette.eventModal.neutralChipBackground
          }
          borderColor={
            options.allowAccentColor && item.backgroundColor
              ? alpha(item.backgroundColor, 0.28)
              : calendarUiPalette.eventModal.outlinedChipBorder
          }
          color={calendarUiPalette.eventModal.primaryText}
          leftAccentColor={options.allowAccentColor && options.showAccentRail ? item.backgroundColor : undefined}
        />
      ))}
      {hiddenCount > 0 ? <CalendarSnapshotChip label={`+${hiddenCount} more`} /> : null}
    </Box>
  );
}

function getServiceRoute(serviceId: string) {
  return RouteInformation.serviceLog.path.replace(/\/$/, '') + `/${serviceId}`;
}

function renderLinkedServiceCollection(
  items: CalendarEventLinkedItem[],
  onOpenService: (serviceId: string) => void,
  maxVisible = DEFAULT_VISIBLE_CHIPS
) {
  const normalizedItems = normalizeRenderableItems(items);

  if (normalizedItems.length === 0) {
    return null;
  }

  const { visibleItems, hiddenCount } = getVisibleItems(normalizedItems, maxVisible);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'flex-start' }}>
      {visibleItems.map((item) => (
        <CalendarSnapshotChip
          key={`${item.id ?? item.name}-linked-service`}
          label={item.name}
          backgroundColor={alpha(logPalette.service.main, 0.08)}
          borderColor={alpha(logPalette.service.main, 0.2)}
          color={logPalette.service.dark}
          onClick={item.id ? () => onOpenService(item.id!) : undefined}
        />
      ))}
      {hiddenCount > 0 ? <CalendarSnapshotChip label={`+${hiddenCount} more`} /> : null}
    </Box>
  );
}

function renderToneChip(tone?: string | null) {
  if (!isPresent(tone)) {
    return null;
  }

  const normalizedTone = tone?.trim();
  let toneStyles;

  if (normalizedTone === Tone.POSITIVE) {
    toneStyles = {
      backgroundColor: alpha(appPalette.tone.positive, 0.14),
      borderColor: alpha(appPalette.tone.positive, 0.28),
      color: appPalette.tone.positive,
    };
  } else if (normalizedTone === Tone.NEGATIVE) {
    toneStyles = {
      backgroundColor: alpha(appPalette.tone.negative, 0.14),
      borderColor: alpha(appPalette.tone.negative, 0.24),
      color: appPalette.tone.negative,
    };
  } else {
    toneStyles = {
      backgroundColor: alpha(appPalette.tone.neutral, 0.12),
      borderColor: alpha(appPalette.tone.neutral, 0.22),
      color: appPalette.tone.neutral,
    };
  }

  return (
    <CalendarSnapshotChip
      label={normalizedTone ?? ''}
      backgroundColor={toneStyles.backgroundColor}
      borderColor={toneStyles.borderColor}
      color={toneStyles.color}
    />
  );
}

function renderTextValue(value?: string | null) {
  if (!isPresent(value)) {
    return null;
  }

  return <Typography sx={detailTextSx}>{value}</Typography>;
}

function getTeamMemberItems(teamMembers?: string | null) {
  return (teamMembers ?? '')
    .split(',')
    .map((member) => member.trim())
    .filter(Boolean);
}

function renderPreviewSection(label: string, htmlOrText?: string | null) {
  const previewText = truncateText(parsePreviewText(htmlOrText));

  if (!previewText) {
    return null;
  }

  return (
    <FieldBlock label={label}>
      <Typography
        sx={{
          ...detailTextSx,
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          lineHeight: 1.58,
        }}
      >
        {previewText}
      </Typography>
    </FieldBlock>
  );
}

function renderTeamMembersField(teamMembers?: string | null) {
  const teamMemberItems = getTeamMemberItems(teamMembers);
  const teamMemberChips = renderChipCollection(teamMemberItems, { maxVisible: 6 });

  if (!teamMemberChips) {
    return null;
  }

  return <FieldBlock label="Team Members">{teamMemberChips}</FieldBlock>;
}

function renderSecondaryContextFields(event: CalendarEvent) {
  const campaigns = renderChipCollection(event.campaignItems ?? [], {
    allowAccentColor: true,
    showAccentRail: true,
    maxVisible: 5,
  });
  const units = renderChipCollection(event.unitItems ?? [], { maxVisible: 5 });
  const departments = renderChipCollection(event.departmentItems ?? [], { maxVisible: 5 });

  if (!campaigns && !units && !departments) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 2,
        alignItems: 'start',
      }}
    >
      {campaigns ? <FieldBlock label="Campaign">{campaigns}</FieldBlock> : null}
      {units ? <FieldBlock label="Unit">{units}</FieldBlock> : null}
      {departments ? <FieldBlock label="Department">{departments}</FieldBlock> : null}
    </Box>
  );
}

function renderAssetSummary(event: CalendarEvent) {
  const fileCount = Array.isArray(event.comms) ? event.comms.length : 0;

  switch (event.logtype) {
    case TrackerDataType.SERVICE:
      return fileCount > 0 ? <CalendarSnapshotChip label={`${fileCount} Comms Material`} /> : null;
    case TrackerDataType.MEDIA_INTERACTION:
      return fileCount > 0 ? (
        <CalendarSnapshotChip label={`${fileCount} Story Link${fileCount === 1 ? '' : 's'}`} />
      ) : null;
    case TrackerDataType.ENGAGEMENT:
      return fileCount > 0 ? (
        <CalendarSnapshotChip label={`${fileCount} Attachment${fileCount === 1 ? '' : 's'}`} />
      ) : null;
    case TrackerDataType.CONTENT_LOG: {
      const hasFileSummary = fileCount > 0;
      const hasLiveLink = Boolean(event.hasLiveLink);
      if (!hasFileSummary && !hasLiveLink) {
        return null;
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {hasFileSummary ? <CalendarSnapshotChip label={`${fileCount} Drafts / Images / Links`} /> : null}
          {hasLiveLink ? (
            <CalendarSnapshotChip
              label="Live Link added"
              backgroundColor={alpha(logPalette.content.main, 0.08)}
              borderColor={alpha(logPalette.content.main, 0.18)}
              color={logPalette.content.main}
            />
          ) : null}
        </Box>
      );
    }
    default:
      return null;
  }
}

function renderServiceSnapshot(event: Extract<CalendarEvent, { logtype: TrackerDataType.SERVICE }>) {
  const campaigns = renderChipCollection(event.campaignItems ?? [], {
    allowAccentColor: true,
    showAccentRail: true,
  });
  const units = renderChipCollection(event.unitItems ?? []);
  const departments = renderChipCollection(event.departmentItems ?? []);
  const teamMembersField = renderTeamMembersField(event.teamMembers);
  const complexityColorMap = {
    '1': appPalette.complexity.one,
    '2': appPalette.complexity.two,
    '3': appPalette.complexity.three,
    '4': appPalette.complexity.four,
    '5': appPalette.complexity.five,
  } as const;
  const complexityColor =
    complexityColorMap[event.complexity?.trim() as keyof typeof complexityColorMap] ?? appPalette.complexity.three;
  const complexityField = isPresent(event.complexity) ? (
    <FieldBlock label="Complexity">
      <CalendarSnapshotChip
        label={event.complexity}
        backgroundColor={complexityColor}
        borderColor={complexityColor}
        color={neutralPalette.black}
      />
    </FieldBlock>
  ) : null;

  return (
    <>
      {campaigns ? <FieldBlock label="Campaign">{campaigns}</FieldBlock> : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        {units ? <FieldBlock label="Unit">{units}</FieldBlock> : null}
        {departments ? <FieldBlock label="Department">{departments}</FieldBlock> : null}
        {teamMembersField}
        {complexityField}
      </Box>
      {renderPreviewSection('Background', event.backgroundHtml)}
    </>
  );
}

function renderMediaInteractionSnapshot(
  event: Extract<CalendarEvent, { logtype: TrackerDataType.MEDIA_INTERACTION }>,
  onOpenService: (serviceId: string) => void
) {
  const linkedServices = renderLinkedServiceCollection(event.linkedServiceItems ?? [], onOpenService);
  const teamMembersField = renderTeamMembersField(event.teamMembers);
  const secondaryContextFields = renderSecondaryContextFields(event);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        {isPresent(event.journalist) ? (
          <FieldBlock label="Journalist">{renderTextValue(event.journalist)}</FieldBlock>
        ) : null}
        {isPresent(event.outlet) ? <FieldBlock label="Outlet">{renderTextValue(event.outlet)}</FieldBlock> : null}
        {renderToneChip(event.tone) ? <FieldBlock label="Tone">{renderToneChip(event.tone)}</FieldBlock> : null}
        {linkedServices ? <FieldBlock label="Linked Service">{linkedServices}</FieldBlock> : null}
        {teamMembersField}
      </Box>
      {secondaryContextFields}
      {renderPreviewSection('Response', event.backgroundHtml)}
    </>
  );
}

function renderEngagementSnapshot(
  event: Extract<CalendarEvent, { logtype: TrackerDataType.ENGAGEMENT }>,
  onOpenService: (serviceId: string) => void
) {
  const linkedServices = renderLinkedServiceCollection(event.linkedServiceItems ?? [], onOpenService);
  const teamMembersField = renderTeamMembersField(event.teamMembers);
  const secondaryContextFields = renderSecondaryContextFields(event);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        {isPresent(event.organization) ? (
          <FieldBlock label="Organization">{renderTextValue(event.organization)}</FieldBlock>
        ) : null}
        {isPresent(event.communityMember) ? (
          <FieldBlock label="Community Member">{renderTextValue(event.communityMember)}</FieldBlock>
        ) : null}
        {renderToneChip(event.tone) ? <FieldBlock label="Tone">{renderToneChip(event.tone)}</FieldBlock> : null}
        {linkedServices ? <FieldBlock label="Linked Service">{linkedServices}</FieldBlock> : null}
        {teamMembersField}
      </Box>
      {secondaryContextFields}
      {renderPreviewSection('Background', event.backgroundHtml)}
    </>
  );
}

function renderContentSnapshot(
  event: Extract<CalendarEvent, { logtype: TrackerDataType.CONTENT_LOG }>,
  onOpenService: (serviceId: string) => void
) {
  const linkedServices = renderLinkedServiceCollection(event.linkedServiceItems ?? [], onOpenService);
  const audiences = renderChipCollection(event.audienceItems ?? [], { allowAccentColor: true });
  const teamMembersField = renderTeamMembersField(event.teamMembers);
  const secondaryContextFields = renderSecondaryContextFields(event);

  let linkedServiceOrTopicField = null;

  if (linkedServices) {
    linkedServiceOrTopicField = <FieldBlock label="Linked Service">{linkedServices}</FieldBlock>;
  } else if (isPresent(event.primaryTitle)) {
    linkedServiceOrTopicField = <FieldBlock label="Topic">{renderTextValue(event.primaryTitle)}</FieldBlock>;
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        {linkedServiceOrTopicField}
        {audiences ? <FieldBlock label="Audience">{audiences}</FieldBlock> : null}
        {teamMembersField}
      </Box>
      {secondaryContextFields}
      {renderPreviewSection('Notes', event.keyMessaging || event.backgroundHtml)}
    </>
  );
}

function renderSnapshotFields(event: CalendarEvent, onOpenService: (serviceId: string) => void) {
  switch (event.logtype) {
    case TrackerDataType.SERVICE:
      return renderServiceSnapshot(event);
    case TrackerDataType.MEDIA_INTERACTION:
      return renderMediaInteractionSnapshot(event, onOpenService);
    case TrackerDataType.ENGAGEMENT:
      return renderEngagementSnapshot(event, onOpenService);
    case TrackerDataType.CONTENT_LOG:
      return renderContentSnapshot(event, onOpenService);
    default:
      return null;
  }
}

export default function EventModal({ event, onClose }: Readonly<EventModalProps>) {
  const navigate = useNavigate();

  const normalizedEvent = useMemo(() => {
    if (!event) {
      return null;
    }

    return {
      ...event,
      backgroundHtml: DOMPurify.sanitize(event.backgroundHtml ?? ''),
    };
  }, [event]);

  if (!normalizedEvent) {
    return null;
  }

  const palette = getModalCategoryPalette(normalizedEvent.logtype);
  const LogIcon = getLogIcon(normalizedEvent.logtype);
  const headerMetaLine = getHeaderMetaLine(normalizedEvent);
  const statusChip = renderStatusChip(normalizedEvent.status);
  const typeItems = normalizedEvent.typeItems ?? [];
  const { visibleItems: visibleTypeItems, hiddenCount: hiddenTypeCount } = getVisibleItems(typeItems, 4);
  const assetSummary = renderAssetSummary(normalizedEvent);

  const handleOpenFullLog = () => {
    navigate(getDetailRoute(normalizedEvent));
    onClose();
  };

  const handleOpenLinkedService = (serviceId: string) => {
    navigate(getServiceRoute(serviceId));
    onClose();
  };

  return (
    <Dialog
      open={Boolean(normalizedEvent)}
      onClose={onClose}
      data-cy="calendar-event-modal"
      maxWidth={false}
      fullWidth={false}
      keepMounted
      PaperProps={{
        sx: {
          width: `min(760px, calc(100vw - ${MODAL_OUTER_GAP_PX}px))`,
          maxHeight: 'calc(100vh - 48px)',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: calendarUiPalette.shadows.modal,
          m: 0,
          backgroundColor: neutralPalette.surfacePage,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <Box sx={{ height: '7px', width: '100%', backgroundColor: palette.buttonColor }} />

      <Box
        sx={{
          px: 5,
          pt: 5,
          pb: 2.5,
          flex: '0 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(neutralPalette.textHelper, 0.26),
            borderRadius: 999,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '999px',
                backgroundColor: palette.buttonColor,
                color: neutralPalette.white,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: calendarUiPalette.eventModal.avatarShadow,
                mt: '1px',
              }}
            >
              <LogIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  ...sectionLabelSx,
                  color: palette.titleColor,
                  mb: '8px',
                }}
              >
                {categoryLabelMap[normalizedEvent.logtype]}
              </Typography>
              <Typography
                id="calendar-event-dialog-title"
                sx={{
                  ...semanticTypography.modalTitle,
                  color: palette.titleColor,
                  fontSize: 'clamp(24px, 2.7vw, 30px)',
                  lineHeight: 1.07,
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  maxWidth: '660px',
                  mb: '8px',
                  wordBreak: 'break-word',
                }}
              >
                {normalizedEvent.primaryTitle || 'Untitled'}
              </Typography>
              {headerMetaLine ? (
                <Typography sx={{ ...headerMetaTextSx, mt: '2px', mb: '12px' }}>{headerMetaLine}</Typography>
              ) : null}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 1, columnGap: 1, alignItems: 'center' }}>
                {statusChip}
                {statusChip && visibleTypeItems.length > 0 ? (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: '1px',
                      minHeight: '20px',
                      alignSelf: 'stretch',
                      backgroundColor: alpha(neutralPalette.borderMuted, 0.9),
                      mx: 0.25,
                    }}
                  />
                ) : null}
                {visibleTypeItems.map((type) => (
                  <CalendarTypeChip
                    key={`${type.name}-${type.backgroundColor ?? 'default'}`}
                    name={type.name}
                    backgroundColor={type.backgroundColor}
                    sx={getHeaderTypeChipSx()}
                  />
                ))}
                {hiddenTypeCount > 0 ? (
                  <CalendarSnapshotChip
                    label={`+${hiddenTypeCount} more`}
                    backgroundColor={alpha(neutralPalette.surfaceRaised, 0.92)}
                    borderColor={alpha(neutralPalette.borderMuted, 0.92)}
                    color={neutralPalette.textSecondary}
                  />
                ) : null}
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="close dialog"
            sx={{
              color: calendarUiPalette.eventModal.closeButton,
              border: `1px solid ${alpha(calendarUiPalette.eventModal.closeButtonBorder, 0.72)}`,
              borderRadius: '12px',
              bgcolor: alpha(calendarUiPalette.eventModal.closeButtonBackground, 0.78),
              flexShrink: 0,
              p: 0.8,
              mt: 0.25,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: 2.25,
            ...getInnerCardStyles(normalizedEvent.logtype),
            px: 2.4,
            py: 2.2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.1 }}>
            {renderSnapshotFields(normalizedEvent, handleOpenLinkedService)}
            {assetSummary ? <FieldBlock label="Assets & Links">{assetSummary}</FieldBlock> : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 5,
          py: 1.6,
          borderTop: `1px solid ${alpha(calendarUiPalette.eventModal.sectionCardBorder, 0.8)}`,
          justifyContent: 'flex-end',
          display: 'flex',
          flexShrink: 0,
          backgroundColor: alpha(neutralPalette.surfaceCard, 0.92),
        }}
      >
        <Button
          variant="contained"
          onClick={handleOpenFullLog}
          data-cy="calendar-event-modal-open-full-log"
          endIcon={<LaunchIcon />}
          sx={getOpenFullLogButtonSx(normalizedEvent.logtype)}
        >
          Open Full Log
        </Button>
      </Box>
    </Dialog>
  );
}
